import { Navigate, Outlet } from 'react-router-dom';
import { useModuleGate } from '../../lib/moduleGate';
import { useWorkspaceModulesReady } from '../../lib/workspaceModulesService';
import { type FeatureFlagValue } from '../../lib/featureFlags';

interface ModuleRouteGuardProps {
  moduleKey: string;
  /**
   * Retained for call-site readability. The registry's own module→flag pairing
   * is what resolves inside `useModuleGate` (build contract §2.7), so this
   * never needs to disagree with the registry.
   */
  featureFlagKey?: FeatureFlagValue;
  redirectTo?: string;
  children?: React.ReactNode;
}

/**
 * Blocks a route (or outlet) when the workspace module is disabled.
 *
 * Resolution waits for `useWorkspaceModulesReady()`. Before it is ready the
 * workspace feature map reads as `{}` and `isModuleEnabled` falls through to
 * the registry's `defaultEnabled` — so a guard that decided on first render
 * bounced every `defaultEnabled: false` module on any cold load (a pasted
 * link, a hard refresh, a link opened in a new tab), regardless of the
 * workspace's real setting. Clicking through in-app happened to work only
 * because the cache was already warm.
 */
export function ModuleRouteGuard({
  moduleKey,
  redirectTo = '/prioritization',
  children,
}: ModuleRouteGuardProps) {
  const ready = useWorkspaceModulesReady();
  const accessible = useModuleGate(moduleKey);

  // Hold the route rather than redirect on a not-yet-known answer. `ready`
  // resolves for a failed fetch too, so this cannot hang indefinitely.
  if (!ready) return null;
  if (!accessible) {
    return <Navigate to={redirectTo} replace />;
  }
  if (children) return <>{children}</>;
  return <Outlet />;
}
